import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import TagStatus from "../../components/TagStatus";
import TableReservationsMobile from "./TableReservationsMobile";
import { formatDateTime, statusSegments } from "./reservationShared";
import { useResponsive } from "../../hook/useResponsive";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import {
  extractValues,
  updateUrlParams,
} from "../../validation/ValidationInput";
import { getLocations } from "../../services/locations.services";
import { useDebounceEffect } from "../../hook/useDebounceEffect";
import GeneralReportIcon from "../../images/GeneralReportIcon";
import ViewIcon from "../../images/ViewIcon";
import {
  ListColumn,
  ListFilter,
  ListQuery,
  ListRowAction,
  ListScreen,
  PAGE_SIZES,
  cells,
  fromSearchTime,
  toSearchTime,
} from "../../components/list";
import {
  getTableReservations,
  RESERVATION_SOURCE_LABELS,
  RESERVATION_STATUS_LABELS,
  ReservationSource,
  ReservationStatus,
  TableReservation,
  TableReservationListParams,
  updateTableReservation,
} from "../../services/tableReservations.service";

/**
 * Back-office list of `dbo.table_reservations`. From 1024px up it is the list kit's
 * ListScreen; below that, TableReservationsMobile (cards, filter sheet, quick status).
 */

interface LocationOption {
  id: string;
  name: string;
  locationCode?: string;
}

/** The list state both layouts keep in the URL, under the kit's keys, so a resize keeps the query. */
interface ReservationQuery {
  page: number;
  limit: number;
  search: string;
  status: string;
  locationId: string;
  source: string;
  /** Reserved-on day, dd.MM.yyyy (the kit's date filter value). */
  date: string;
}

const enumParam = (value: string): number | undefined =>
  value === "" || !Number.isFinite(Number(value)) ? undefined : Number(value);

/** A reserved-on day as the local [00:00, 23:59:59.999] range the API filters `reservedFrom` by. */
const dayRange = (day: string): { dateFrom?: string; dateTo?: string } => {
  const start = fromSearchTime(day);
  if (!start) return {};
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { dateFrom: start.toISOString(), dateTo: end.toISOString() };
};

const listParams = (q: ReservationQuery): TableReservationListParams => ({
  page: q.page,
  limit: q.limit,
  locationId: q.locationId || undefined,
  status: enumParam(q.status),
  source: enumParam(q.source),
  search: q.search || undefined,
  ...dayRange(q.date),
});

const readUrlQuery = (): ReservationQuery => {
  const params: any = extractValues(location.search.split("&"));
  const limit = +params.limit;
  return {
    page: +params.page || 1,
    limit: PAGE_SIZES.includes(limit) ? limit : 50,
    search: params.search ?? "",
    status: params.status ?? "",
    locationId: params.locationId ?? "",
    source: params.source ?? "",
    date: params.date ?? "",
  };
};

const urlOf = (q: ReservationQuery) =>
  updateUrlParams(
    { searchValue: q.search, page: q.page, limit: q.limit },
    { status: q.status, locationId: q.locationId, source: q.source, date: q.date }
  );

interface TouchProps {
  language: any;
  locations: LocationOption[];
  isTablet: boolean;
  onCreate: () => void;
  onOpen: (id: string) => void;
  emptyState: { title?: string; desc?: string };
}

/** The touch layout: page-owned query state (the kit's ListMobile has no quick status). */
const TouchReservations: React.FC<TouchProps> = ({
  language,
  locations,
  isTablet,
  onCreate,
  onOpen,
  emptyState,
}) => {
  const [filter, setFilter] = useState<ReservationQuery>(readUrlQuery);
  const filterRef = useRef<ReservationQuery>(filter);
  /** True while the last change was typed into the search box (debounced). */
  const typingRef = useRef<boolean>(false);
  const requestSeq = useRef<number>(0);
  const [rows, setRows] = useState<TableReservation[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const t = (key: string): string => (language && language[key]) || key;

  const fetchList = async (val: ReservationQuery) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    webLoadingFalse();
    try {
      const response = await getTableReservations(listParams(val));
      if (seq !== requestSeq.current) return;
      setRows(response.data ?? []);
      setCount(response.count ?? 0);
    } catch (error) {
      console.error("Error loading table reservations:", error);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(filterRef.current);
    const handleNewToken = () => fetchList(filterRef.current);
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, []);

  useDebounceEffect(
    () => {
      if (!typingRef.current) return;
      typingRef.current = false;
      fetchList(filterRef.current);
    },
    [filter],
    700
  );

  /** One history entry per deliberate action (filter, page, clear), fetched at once. */
  const apply = (next: ReservationQuery) => {
    typingRef.current = false;
    filterRef.current = next;
    setFilter(next);
    webNavigate(urlOf(next));
    fetchList(next);
  };

  const handleSearchChange = (value: string) => {
    const next = { ...filterRef.current, page: 1, search: value };
    typingRef.current = true;
    filterRef.current = next;
    setFilter(next);
    window.history.replaceState(window.history.state, "", location.pathname + urlOf(next));
  };

  const changeFilter = (key: keyof ReservationQuery, value: string) =>
    apply({ ...filterRef.current, page: 1, [key]: value });

  const handleReservedFromDate = (date: Date | null) =>
    changeFilter("date", date ? toSearchTime(date) : "");

  // "Clear all" also drops the search and status: the host is asking for the whole book back.
  const handleClearFilters = () =>
    apply({
      ...filterRef.current,
      page: 1,
      search: "",
      status: "",
      locationId: "",
      source: "",
      date: "",
    });

  /** One-tap status change from the record sheet: swaps the row in place, no refetch. */
  const handleQuickStatus = async (
    row: TableReservation,
    status: ReservationStatus
  ) => {
    try {
      const updated = await updateTableReservation(row.id, { status });
      setRows((prev) =>
        prev.map((item) => (item.id === row.id ? { ...item, ...updated } : item))
      );
      showToast({
        message: t("Reservation updated"),
        type: "success",
      });
    } catch (err: any) {
      showApiError(err, { language, titleFallback: t("Failed to update reservation") });
    }
  };

  return (
    <div className="h-full relative">
      <TableReservationsMobile
        language={language}
        rows={rows}
        count={count}
        page={filter.page}
        limit={filter.limit}
        loading={loading}
        locations={locations}
        filter={{
          search: filter.search,
          locationId: filter.locationId,
          status: filter.status,
          source: filter.source,
          dateFrom: fromSearchTime(filter.date)?.toISOString() ?? "",
        }}
        isTablet={isTablet}
        onSearchChange={handleSearchChange}
        onStatusChange={(value) => changeFilter("status", value)}
        onLocationChange={(id) => changeFilter("locationId", id)}
        onSourceChange={(value) => changeFilter("source", value)}
        onDateChange={handleReservedFromDate}
        onClearFilters={handleClearFilters}
        onPageChange={(page) => apply({ ...filterRef.current, page })}
        onCreate={onCreate}
        onOpen={onOpen}
        onQuickStatus={handleQuickStatus}
        emptyState={emptyState}
      />
    </div>
  );
};

const TableReservations = () => {
  const [language, setLanguage] = useState<any>(null);
  const [country, setCountry] = useState<LocationOption[]>([]);

  const companyId = localStorage.getItem("companyId");
  const userId = localStorage.getItem("userId");
  const { isTablet, isTouchLayout } = useResponsive();

  /** The repo's i18n pattern: host dictionary first, English literal as the fallback. */
  const t = (key: string): string => (language && language[key]) || key;

  const inforTable: any = {
    img: <GeneralReportIcon />,
    title:
      language && language?.["No table reservations yet"]
        ? language?.["No table reservations yet"]
        : "No table reservations yet",
    desc:
      language &&
      language?.[
        "Reservations will appear here once they're created from POS or integrations."
      ]
        ? language?.[
            "Reservations will appear here once they're created from POS or integrations."
          ]
        : "Reservations will appear here once they're created from POS or integrations.",
  };

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const fetchLocations = async () => {
    try {
      // page/limit must be explicit: the back end defaults `limit` to 10 when
      // it cannot parse the value, so the old `""` silently returned the first
      // ten locations only and every booking outside them rendered "-".
      const response: any = await getLocations(userId, companyId, 1, 200, "");
      setCountry(response?.data?.data ?? []);
    } catch (error) {
      console.error("Error loading locations:", error);
    }
  };

  useEffect(() => {
    fetchLocations();
    const handleNewToken = () => {
      fetchLocations();
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, []);

  const preLanguage = localStorage.getItem("language") || "";
  const handleCreate = () => {
    webNavigate(`/${preLanguage}/tableReservations/create`);
  };

  const handleOpenReservation = (reservationId: string) => {
    webNavigate(`/${preLanguage}/tableReservations/${reservationId}`);
  };

  if (isTouchLayout) {
    return (
      <TouchReservations
        language={language}
        locations={country}
        isTablet={isTablet}
        onCreate={handleCreate}
        onOpen={handleOpenReservation}
        emptyState={{ title: inforTable.title, desc: inforTable.desc }}
      />
    );
  }

  /** One page of bookings; the search box maps onto `search` (number, name, email, phone). */
  const fetchReservations = async (query: ListQuery) => {
    webLoadingFalse();
    const response = await getTableReservations(
      listParams({
        page: query.page,
        limit: query.limit,
        search: query.search,
        status: query.filters.status ?? "",
        locationId: query.filters.locationId ?? "",
        source: query.filters.source ?? "",
        date: query.filters.date ?? "",
      })
    );
    return { rows: response.data ?? [], count: response.count ?? 0 };
  };

  const locationName = (id: string) =>
    country.find((loc) => loc.id === id)?.name || "-";
  const statusLabel = (row: TableReservation) =>
    RESERVATION_STATUS_LABELS[row.status as ReservationStatus] ?? "-";
  const sourceLabel = (row: TableReservation) =>
    RESERVATION_SOURCE_LABELS[row.source as ReservationSource] ?? "-";

  /* ---------------- List kit configuration ---------------- */

  const columns: ListColumn<TableReservation>[] = [
    {
      key: "reservationNumber",
      label: "Reservation",
      width: 150,
      hideable: false,
      render: cells.primary((r) => r.reservationNumber),
      value: (r) => r.reservationNumber,
      mobile: "subtitle",
      onClick: (r) => handleOpenReservation(r.id),
    },
    {
      key: "customerName",
      label: "Customer",
      width: 200,
      value: (r) => r.customerName,
      mobile: "title",
    },
    {
      key: "partySize",
      label: "Party",
      width: 80,
      value: (r) => r.partySize,
      mobile: "sheet",
    },
    // A booking's clock time matters in any year, so not cells.date (which drops it for other years).
    {
      key: "reservedFrom",
      label: "From",
      width: 150,
      value: (r) => formatDateTime(r.reservedFrom),
      mobile: "sheet",
    },
    {
      key: "reservedTo",
      label: "To",
      width: 150,
      value: (r) => formatDateTime(r.reservedTo),
      mobile: "sheet",
    },
    {
      key: "locationId",
      label: "Location",
      width: 170,
      value: (r) => locationName(r.locationId),
      mobile: "sheet",
    },
    {
      key: "status",
      label: "Status",
      width: 130,
      value: (r) => t(statusLabel(r)),
      render: (r) => <TagStatus value={statusLabel(r)} label={t(statusLabel(r))} />,
      mobile: "trailing",
    },
    {
      key: "source",
      label: "Source",
      width: 120,
      value: (r) => t(sourceLabel(r)),
      mobile: "sheet",
    },
  ];

  const filters: ListFilter[] = [
    { key: "status", label: "Status", kind: "select", options: statusSegments(language) },
    {
      key: "locationId",
      label: "Location",
      kind: "select",
      options: country.map((loc) => ({ value: loc.id, label: loc.name })),
    },
    {
      key: "source",
      label: "Source",
      kind: "select",
      options: Object.entries(RESERVATION_SOURCE_LABELS).map(([value, label]) => ({
        value,
        label: t(label),
      })),
    },
    { key: "date", label: "Reserved on", kind: "date" },
  ];

  const rowActions: ListRowAction<TableReservation>[] = [
    {
      key: "view",
      label: "View",
      icon: <ViewIcon />,
      sheetIcon: "chev",
      onRun: (r) => handleOpenReservation(r.id),
    },
  ];

  return (
    <div
      className="h-full relative"
      style={{ display: "flex", flexDirection: "column" }}
    >
      <div
        className="flex items-center justify-between"
        style={{ gap: "16px" }}
      >
        <p
          style={{ color: "#222525" }}
          className="text-[24px] leading-[130%] font-medium font-inter"
        >
          {language && language?.["Table Reservations"]
            ? language?.["Table Reservations"]
            : "Table Reservations"}
        </p>
        <button
          type="button"
          onClick={handleCreate}
          style={{
            borderRadius: "30px",
            color: "#FFFFFF",
            fontSize: "14px",
            fontWeight: 500,
            backgroundColor: "#0F7679",
            padding: "10px 20px",
            border: "none",
            cursor: "pointer",
            whiteSpace: "nowrap",
            fontFamily: "Inter, sans-serif",
          }}
        >
          +{" "}
          {language && language?.["Create reservation"]
            ? language?.["Create reservation"]
            : "Create reservation"}
        </button>
      </div>
      <ListScreen
        language={language}
        columns={columns}
        fetchRows={fetchReservations}
        searchPlaceholder="Name, email or phone"
        filters={filters}
        rowActions={rowActions}
        countNoun="reservations"
        emptyState={inforTable}
      />
    </div>
  );
};

export default TableReservations;
